import { Box, Typography } from '@mui/material';
import { PaymentElement } from '@stripe/react-stripe-js';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../Button';
import {
	useInvoiceAutoPayUpdate,
	useInvoiceAutoPayUpdateInvoiceFragment,
} from './useInvoiceAutoPayUpdate';
import styles from './InvoiceViewAutoPayForm.module.scss';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { useInvoiceAutopay } from '../useInvoiceAutopay';
import { DocumentNode, gql } from '@apollo/client';
import {
	InvoiceViewAutoPayFormInvoiceFragment,
	InvoiceViewAutoPayFormOrganizationFragment,
	PaymentMethodType,
	useInvoiceViewAutoPayForm_GetStripePaymentCapabilitiesQuery,
} from '#types';
import PaymentMethodSelect from '../../../../PaymentMethodSelect';
import { useSelector } from 'react-redux';
import { FlattenedUser } from '../../../../../types/user';
import { useDeviceDetection } from '../../../../../hooks/useDeviceDetection';
import { getStatementDescriptorCopy } from '../../../../../mui/MuiPaymentMethod/helpers';
import rpcShared from '@rockpapercoin/rpc-shared';

type StripeCapabilities = Parameters<
	typeof rpcShared.invoices.hasOBOCapabilities
>[0];

const isStripeCapabilities = ( capabilities: {
	card_payments?: string | null;
	acss_debit_payments?: string | null;
} ): capabilities is StripeCapabilities =>
	capabilities.card_payments !== null &&
	capabilities.acss_debit_payments !== null;

export const invoiceViewAutoPayFormInvoiceFragment = gql`
	fragment invoiceViewAutoPayFormInvoice on Invoice {
		...useInvoiceAutoPayUpdateInvoice
	}
	${ useInvoiceAutoPayUpdateInvoiceFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getStripePaymentCapabilitiesQuery = gql`
	query InvoiceViewAutoPayForm_GetStripePaymentCapabilities(
		$where: OrganizationWhereUniqueInput!
	) {
		getStripePaymentCapabilities(where: $where) {
			card_payments
			acss_debit_payments
		}
	}
`;

const invoiceViewAutoPayFormOrganizationFragment = gql`
	fragment invoiceViewAutoPayFormOrganization on Organization {
		id
		name
		cardConvenienceFeePercentage
		achConvenienceFeePercentage
	}
`;

export type InvoiceViewAutoPayFormProps = {
	invoiceId: string;
	invoice?: InvoiceViewAutoPayFormInvoiceFragment;
	guestToken?: string;
	stripeElementOptions: StripeElementsOptions;
	organization?: InvoiceViewAutoPayFormOrganizationFragment | null;
} & Pick<
ReturnType<typeof useInvoiceAutopay>,
'autopayIsActive' | 'onDeactivate' | 'onActivateOrUpdate'
>;

type InvoiceViewAutoPayFormPropsWithFragment =
	React.FC<InvoiceViewAutoPayFormProps> & {
		fragments: {
			invoiceViewAutoPayFormInvoiceFragment: DocumentNode;
			invoiceViewAutoPayFormOrganizationFragment: DocumentNode;
		};
	};

export const InvoiceViewAutoPayForm: InvoiceViewAutoPayFormPropsWithFragment =
	( {
		invoiceId,
		invoice,
		guestToken,
		onDeactivate,
		autopayIsActive,
		stripeElementOptions,
		onActivateOrUpdate,
		organization,
	} ) => {
		const [ componentLoading, setComponentLoading ] = useState( true );
		const user: FlattenedUser = useSelector( ( state: any ) => state.user );

		const {
			onSubmit,
			loading: stripeLoading,
			paymentMethodType,
			setPaymentMethodType,
			paymentMethodOrder,
			billing_details,
		} = useInvoiceAutoPayUpdate( {
			invoiceId,
			invoice,
			guestToken,
			stripeElementOptions,
			onActivateOrUpdate,
		} );

		useEffect( () => {
			setComponentLoading( true );
			return () => setComponentLoading( true );
		}, [] );

		const { data: stripePaymentCapabilitiesData } =
			useInvoiceViewAutoPayForm_GetStripePaymentCapabilitiesQuery( {
				variables: { where: { id: organization?.id || '' } },
				skip: !organization?.id,
			} );

		const stripeCapabilities =
			stripePaymentCapabilitiesData?.getStripePaymentCapabilities;
		const hasInternationalPayments =
			stripeCapabilities && isStripeCapabilities( stripeCapabilities )
				? rpcShared.invoices.hasOBOCapabilities( stripeCapabilities )
				: false;

		const statementDescriptorCopy = useMemo(
			() =>
				getStatementDescriptorCopy( {
					orgName: organization?.name || '',
					paymentMethodType: paymentMethodType || PaymentMethodType.card,
					hasInternationalPayments,
				} ),
			[
				organization?.name,
				paymentMethodType,
				hasInternationalPayments
			]
		);

		const { isPhone } = useDeviceDetection();

		return (
			<form className={ styles.container } onSubmit={ onSubmit }>
				<PaymentMethodSelect
					paymentMethodType={ paymentMethodType }
					setPaymentMethodType={ setPaymentMethodType }
					cardConvenienceFeePercentage={
						organization?.cardConvenienceFeePercentage ?? 0
					}
					achConvenienceFeePercentage={
						organization?.achConvenienceFeePercentage ?? 0
					}
					disabled={ !!paymentMethodType }
					className={ styles.paymentMethodSelect }
				/>
				{ paymentMethodOrder ? (
					<>
						<PaymentElement
							options={ {
								paymentMethodOrder,
								layout: {
									type: isPhone ? 'accordion' : 'tabs',
									/* Don't collapse the payment method select if the user is not logged in.
									They're going to have to enter a payment method anyway... */
									defaultCollapsed: user?.isLoggedIn,
								},
								defaultValues: {
									billingDetails: billing_details,
								},
								fields: {
									billingDetails: {
										email: 'never',
										name: 'never',
									},
								},
								terms: {
									card: 'never',
									usBankAccount: 'never',
									applePay: 'never',
									googlePay: 'never',
								},
							} }
							onChange={ ( e ) => {
								// to account for Link, we adjust undefined to be card
								const paymentMethodType =
									PaymentMethodType[
										e.value.type as keyof typeof PaymentMethodType
									];
								setPaymentMethodType( paymentMethodType );
							} }
							onReady={ () => setComponentLoading( false ) }
						/>
						<Typography
							className={ styles.statementDescriptor }
							data-cy='statement-descriptor-copy'
						>
							{ statementDescriptorCopy }
						</Typography>
						<Box className={ styles.submitButtonBox }>
							<Button
								type='submit'
								loading={ componentLoading || stripeLoading }
								data-cy='autopay-submit-button'
							>
								{ `${ autopayIsActive ? 'Update' : 'Activate' } Autopay` }
							</Button>
							{ autopayIsActive ? (
								<Button
									type='submit'
									loading={ stripeLoading }
									variant='outlined'
									data-cy='autopay-disable-button'
									onClick={ onDeactivate }
								>
									Turn Off Autopay
								</Button>
							) : null }
						</Box>
					</>
				) : autopayIsActive ? (
					<Box className={ styles.submitButtonBox }>
						<Button
							type='submit'
							loading={ stripeLoading }
							variant='outlined'
							data-cy='autopay-disable-button'
							onClick={ onDeactivate }
						>
							Turn Off Autopay
						</Button>
					</Box>
				) : null }
			</form>
		);
	};

InvoiceViewAutoPayForm.fragments = {
	invoiceViewAutoPayFormOrganizationFragment,
	invoiceViewAutoPayFormInvoiceFragment,
};
